import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

import { API_URL } from '../utils/apiConfig';

// Single user-friendly error message - never show technical details
const FRIENDLY_ERROR_MESSAGE = 'Verification failed. Please request a new sign-in link and try again.';

/**
 * VerifyPage - Handles magic link token verification
 * 
 * Security: NEVER displays technical errors to users.
 * All errors show the same friendly message.
 * Technical details are logged to console only.
 */
export default function VerifyPage() {
  const [status, setStatus] = useState('loading'); // loading | success | error
  
  // Safely destructure auth context with fallbacks
  const auth = useAuth() || {};
  const { verifyMagicLink } = auth;
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const verifyToken = useCallback(async () => {
    // No token provided
    if (!token) {
      console.error('VerifyPage error: No token provided in URL');
      setStatus('error');
      return;
    }

    try {
      setStatus('loading');
      
      // Try using AuthContext's verifyMagicLink if available
      if (typeof verifyMagicLink === 'function') {
        const data = await verifyMagicLink(token);
        
        if (data && data.user) {
          setStatus('success');
          
          const dashboardMap = {
            sender: '/app/sender',
            merchant: '/app/merchant',
            admin: '/app/admin',
          };
          const targetPath = dashboardMap[data.user.role] || '/app/sender';
          
          setTimeout(() => {
            if (typeof navigate === 'function') {
              navigate(targetPath, { replace: true });
            } else {
              window.location.href = targetPath;
            }
          }, 500);
          return;
        }
        
        // If we get here, verification didn't return expected data
        throw new Error('Invalid response from verifyMagicLink');
      }
      
      // Fallback: call API directly if verifyMagicLink is not available
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      // Read body ONLY ONCE
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        // JSON parsing failed - log and throw generic error
        console.error('VerifyPage error: Failed to parse response JSON', parseError);
        throw new Error('Parse error');
      }

      // Check response status AFTER parsing
      if (!response.ok) {
        // Log the actual error detail for debugging
        console.error('VerifyPage error: API returned error', {
          status: response.status,
          detail: data?.detail || data?.message || 'Unknown'
        });
        throw new Error('API error');
      }

      // Verify we got expected data
      if (!data || !data.token || !data.user) {
        console.error('VerifyPage error: Missing token or user in response');
        throw new Error('Invalid response');
      }

      // Success - store token and redirect
      try {
        localStorage.setItem('pecash_token', data.token);
      } catch (storageError) {
        console.error('VerifyPage error: Failed to store token', storageError);
        // Continue anyway - token storage failure shouldn't block login
      }
      
      setStatus('success');
      
      const dashboardMap = {
        sender: '/app/sender',
        merchant: '/app/merchant',
        admin: '/app/admin',
        beneficiary: '/beneficiary',
      };
      const targetPath = dashboardMap[data.user.role] || '/app/sender';
      
      setTimeout(() => {
        if (typeof navigate === 'function') {
          navigate(targetPath, { replace: true });
        } else {
          window.location.href = targetPath;
        }
      }, 500);
      
    } catch (err) {
      // Log the REAL error for debugging - NEVER show to user
      console.error('VerifyPage error:', err);
      
      // Always show the same friendly error - no technical details
      setStatus('error');
    }
  }, [token, verifyMagicLink, navigate]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // ALWAYS render visible UI - never return null
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Loading State */}
        {status === 'loading' && (
          <div data-testid="verify-loading">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Signing you in...</h1>
            <p className="text-slate-600">Please wait while we verify your credentials.</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div data-testid="verify-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Success!</h1>
            <p className="text-slate-600">Redirecting to your dashboard...</p>
          </div>
        )}

        {/* Error State - ALWAYS shows friendly message only */}
        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
            <p className="text-slate-600 mb-6">{FRIENDLY_ERROR_MESSAGE}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
              <p className="text-sm text-slate-500">
                Need help? Contact support at{' '}
                <a href="mailto:support@pecashglobal.com" className="text-emerald-600 hover:underline">
                  support@pecashglobal.com
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
